import { EntityComment } from '../queries/types';

// Regex patterns for entity/IP matching
const IP_ADDRESS_REGEX = /^\d+\.\d+\.\d+\.\d+$/;
const DOT_REPLACEMENT_REGEX = /\./g;

/**
 * Sorts comments to prioritize those mentioning the entity/IP address,
 * then sorts by most recent (created_at DESC) within each group.
 */
export const sortCommentsWithIPMentionsFirst = (
  comments: EntityComment[],
  entityName: string,
): EntityComment[] => {
  if (!comments.length || !entityName) {
    return comments;
  }

  const normalizedEntityName = entityName.toLowerCase().trim();

  // If entityName is whitespace-only, it becomes empty after trim
  if (!normalizedEntityName) {
    return comments;
  }

  /**
   * Checks if a comment text mentions the entity name (IP address).
   * For IP addresses, matches the exact IP to avoid partial matches.
   */
  const mentionsEntity = (comment: string): boolean => {
    const normalizedComment = comment.toLowerCase();

    // Check if entityName looks like an IP address (contains dots)
    if (normalizedEntityName.match(IP_ADDRESS_REGEX)) {
      // IP address - match exact IP with word boundary-like logic
      // This ensures "172.20.0.2" matches but "172.20" doesn't match "172.20.0.2"
      const escapedIP = normalizedEntityName.replace(
        DOT_REPLACEMENT_REGEX,
        '\\.',
      );
      // Match IP at word boundaries (start/end of string, or surrounded by non-digit chars)
      const ipRegex = new RegExp(`(^|[^\\d.])${escapedIP}([^\\d.]|$)`, 'i');
      return ipRegex.test(normalizedComment);
    } else {
      // Non-IP entity - use simple case-insensitive includes
      return normalizedComment.includes(normalizedEntityName);
    }
  };

  const sortByDateDesc = (a: EntityComment, b: EntityComment) =>
    b.created_at - a.created_at;

  const withMentions = comments
    .filter((c) => mentionsEntity(c.comment))
    .sort(sortByDateDesc);
  const withoutMentions = comments
    .filter((c) => !mentionsEntity(c.comment))
    .sort(sortByDateDesc);

  return [...withMentions, ...withoutMentions];
};
